import React, { useRef, useState, useEffect, useCallback } from 'react';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import { validateCredentials } from './formUtils';
import { SIGNATURE_INTERNAL_STATUS } from './formConstants';

const SignatureAuthPage = ({ formData, onDataUpdate, validationErrors }) => {
  const signaturesData = formData.signatures || {};
  const errors = validationErrors || {};
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [signatureMode, setSignatureMode] = useState(signaturesData.signatureMode || 'typed');
  const [hasDrawn, setHasDrawn] = useState(!!signaturesData.signatureDataUrl);

  const handleChange = useCallback((field, value) => {
    onDataUpdate({ signatures: { ...formData.signatures, [field]: value } });
  }, [formData.signatures, onDataUpdate]);

  const handlePrpChange = useCallback((field, value) => {
    onDataUpdate({ prp: { ...(formData.prp || {}), [field]: value } });
  }, [formData.prp, onDataUpdate]);

  const prpData = formData.prp || {};
  const showPRPSupervision = formData.referralCategory === 'PRP' || formData.referralCategory === 'Both';

  // Canvas setup - intentionally depends only on signatureMode to avoid re-render loops
  useEffect(() => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = canvas.offsetWidth;
      canvas.height = 120;
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (signaturesData.signatureDataUrl) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0);
        img.src = signaturesData.signatureDataUrl;
        setHasDrawn(true);
      }
    }
  }, [signatureMode]);

  const getCanvasCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = useCallback(() => {
    if (isDrawingRef.current && canvasRef.current) {
      isDrawingRef.current = false;
      setHasDrawn(true);
      const dataUrl = canvasRef.current.toDataURL();
      onDataUpdate({ signatures: { ...formData.signatures, signatureDataUrl: dataUrl, signatureMode: 'draw' } });
    }
  }, [formData.signatures, onDataUpdate]);

  const clearCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setHasDrawn(false);
      handleChange('signatureDataUrl', '');
    }
  };

  const switchMode = (mode) => {
    setSignatureMode(mode);
    handleChange('signatureMode', mode);
    if (mode === 'typed') {
      handleChange('signatureDataUrl', '');
      setHasDrawn(false);
    } else {
      handleChange('typedSignature', '');
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center border-b-2 border-gray-600 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Signatures & Authorization</h1>
        <p className="text-sm text-gray-600 mt-2">Required signatures to complete referral</p>
      </div>

      {errors.signatures && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="signature-errors">
          Please complete all required fields before submitting.
        </div>
      )}

      {/* Supervision Question (PRP/Both only) */}
      {showPRPSupervision && (
        <section className="bg-orange-50 p-6 rounded-lg border-2 border-orange-300" data-testid="supervision-section">
          <h2 className="text-xl font-bold text-orange-900 mb-4 border-b-2 border-orange-600 pb-2">Referring Clinician Supervision</h2>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Is the Referring Clinician under supervision? <span className="text-red-500">*</span>
            </label>
            <select
              value={prpData.clinicianUnderSupervision || ''}
              onChange={(e) => handlePrpChange('clinicianUnderSupervision', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              data-testid="clinician-supervision-select"
              required
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          {prpData.clinicianUnderSupervision === 'Yes' && (
            <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg" data-testid="supervisor-fields">
              <h3 className="text-sm font-bold text-yellow-800 mb-3">Supervisor Information (Required)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Supervisor Credentials" value={prpData.supervisorCredentials} onChange={(v) => handlePrpChange('supervisorCredentials', v)} required error={errors.supervisorCredentials || (prpData.supervisorCredentials ? validateCredentials(prpData.supervisorCredentials) : '')} />
                <div>
                  <FormInput label="Supervisor NPI Number" value={prpData.supervisorNPI} onChange={(v) => handlePrpChange('supervisorNPI', v)} required error={errors.supervisorNPI} />
                  <a href="https://npiregistry.cms.hhs.gov/search" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 inline-block" data-testid="supervisor-npi-lookup-link">Search for NPI Number</a>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Referring Provider Signature */}
      <section className="bg-blue-50 p-6 rounded-lg border-2 border-blue-300">
        <h2 className="text-xl font-bold text-blue-900 mb-4 border-b-2 border-blue-600 pb-2">Referring Provider Signature</h2>
        <div className="space-y-4">
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => switchMode('typed')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${signatureMode === 'typed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="signature-mode-typed">Type Signature</button>
            <button type="button" onClick={() => switchMode('draw')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${signatureMode === 'draw' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="signature-mode-draw">Draw Signature</button>
          </div>

          {signatureMode === 'typed' && (
            <div className="bg-white border-2 border-gray-300 rounded-lg p-4" data-testid="typed-signature-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Signature <span className="text-red-500">*</span></label>
              <input type="text" value={signaturesData.typedSignature || ''} onChange={(e) => handleChange('typedSignature', e.target.value.toUpperCase())} placeholder="TYPE YOUR FULL NAME" className="w-full px-4 py-3 border-b-2 border-gray-400 text-xl font-bold tracking-widest uppercase focus:border-blue-600 focus:outline-none bg-transparent" style={{ fontFamily: 'monospace' }} data-testid="typed-signature-input" />
              <p className="text-xs text-gray-500 mt-2 italic">Your typed name will serve as your digital signature (block letters)</p>
              {errors.signature && <p className="text-xs text-red-600 mt-1">{errors.signature}</p>}
            </div>
          )}

          {signatureMode === 'draw' && (
            <div className="bg-white border-2 border-gray-300 rounded-lg p-4" data-testid="drawn-signature-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Signature <span className="text-red-500">*</span></label>
              <canvas ref={canvasRef} className="w-full border-2 border-dashed border-gray-300 rounded-lg cursor-crosshair bg-white" style={{ height: 120, touchAction: 'none' }} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-testid="signature-canvas" />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500 italic">{hasDrawn ? 'Signature captured' : 'Draw your signature above'}</p>
                <button type="button" onClick={clearCanvas} className="text-xs text-red-600 hover:text-red-800 font-medium" data-testid="clear-signature-btn">Clear</button>
              </div>
              {errors.signature && <p className="text-xs text-red-600 mt-1">{errors.signature}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Date" type="date" value={signaturesData.providerDate} onChange={(v) => handleChange('providerDate', v)} required error={errors.providerDate} />
          </div>
        </div>
      </section>

      {/* Intake Coordinator Review */}
      <section className="bg-gray-100 p-6 rounded-lg border-2 border-gray-400">
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-600 pb-2">Intake Coordinator Review (Office Use Only)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Intake Coordinator Name" value={signaturesData.coordinatorName} onChange={(v) => handleChange('coordinatorName', v)} />
          <FormInput label="Date Received" type="date" value={signaturesData.dateReceived} onChange={(v) => handleChange('dateReceived', v)} />
          <FormSelect label="Internal Status" value={signaturesData.internalStatus} onChange={(v) => handleChange('internalStatus', v)} options={SIGNATURE_INTERNAL_STATUS} />
        </div>
      </section>

      {/* Authorization Statement */}
      <section className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-400">
        <h3 className="font-bold text-gray-900 mb-3">Authorization Statement</h3>
        <p className="text-sm text-gray-700 leading-relaxed">
          I hereby authorize SDORA WELLBEING to provide mental health services as recommended and to communicate with referring providers and other professionals involved in the client's care. I understand that all information shared is protected under HIPAA regulations and will be used solely for treatment, care coordination, and quality improvement purposes. I acknowledge that I have the right to revoke this authorization at any time in writing.
        </p>
        <div className="mt-4 flex items-start">
          <input type="checkbox" checked={signaturesData.authorizationAgreed || false} onChange={(e) => handleChange('authorizationAgreed', e.target.checked)} required className="w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded mt-0.5" data-testid="authorization-checkbox" />
          <label className="ml-3 text-sm text-gray-700">I have read and agree to the authorization statement above <span className="text-red-500">*</span></label>
        </div>
        {errors.authorizationAgreed && <p className="text-xs text-red-600 mt-2 ml-8">{errors.authorizationAgreed}</p>}
      </section>
    </div>
  );
};

export default SignatureAuthPage;
